<template>
	<ListItem class="c-list-item-employee" v-bind="$props">
		<!-- Image -->
		<template #left>
			<figure
				class="c-list-item-employee__image w-140 h-140 flex-shrink-0"
			>
				<div class="c-list-item-employee__image-form">
					<div class="c-list-item-employee__photo">
						<BaseImg
							v-if="image"
							class="flex-shrink-0"
							:src="image.cropUrl || image.url"
							:alt="image.altText"
							:sizes="nuxtImgSizes('<1441:140px')"
							:width="image.width"
							:height="image.height"
							:ratio="1"
							:load-color="imageLoadColor"
						/>
						<div
							v-else-if="usePlaceholder"
							class="c-list-item-employee__photo--placeholder"
						></div>
					</div>
				</div>
			</figure>
		</template>

		<template #afterHeading>
			<p
				v-if="computedTitle"
				class="font-light text-body-xs whitespace-pre-wrap mt-6"
			>
				<span
					v-for="(str, index) in computedTitle"
					:key="index"
					class="inline-block"
					v-text="str"
				></span>
			</p>

			<!-- Details -->
			<div
				v-if="
					computedPhone ||
					computedEmail ||
					computedDepartments ||
					computedLocation
				"
				class="mt-xs/v flex items-baseline gap-x-xs/h gap-y-4 max-w-max"
			>
				<div
					v-if="computedPhone || computedEmail"
					class="flex flex-col gap-y-4"
				>
					<!-- Phone -->
					<div v-if="computedPhone" class="leading-0">
						<a
							:href="computedPhone.link"
							class="
								text-label
								leading-1.2
								text-red
								hover:underline
								focus:underline
							"
						>
							{{ computedPhone.text }}
						</a>
					</div>

					<!-- Email -->
					<div v-if="computedEmail" class="leading-0">
						<a
							:href="computedEmail.link"
							class="
								text-label
								leading-1.2
								text-red
								hover:underline
								focus:underline
							"
						>
							{{ computedEmail.text }}
						</a>
					</div>
				</div>

				<div
					v-if="computedDepartments || computedLocation"
					class="flex flex-col gap-y-6"
				>
					<!-- Departments -->
					<div
						v-if="computedDepartments"
						class="
							text-label-sm
							leading-1.2
							text-gray-darker
							font-medium
							uppercase
						"
					>
						{{ computedDepartments }}
					</div>

					<!-- Location -->
					<div
						v-if="computedLocation"
						class="
							text-label-sm
							leading-1.2
							text-gray-darker
							font-medium
							uppercase
						"
					>
						{{ computedLocation }}
					</div>
				</div>
			</div>
		</template>

		<template #contentRight="{ hasAccordionContent }">
			<AreaReferences
				v-if="areas"
				class="c-list-item-employee__areas justify-end"
				:class="{
					'pb-48': hasAccordionContent,
				}"
				:items="areas"
				:show-max="3"
			/>
		</template>

		<template #footer>
			<div
				v-if="competencies"
				class="align-baseline text-red text-label-sm"
				style="max-width: 710px; padding-right: 10px"
			>
				<HeadingX
					v-if="competencies.label"
					class="inline uppercase"
					:level-offset="headingLevelOffset + 1"
					v-text="competencies.label + ': '"
				/>
				<ul class="contents">
					<li
						v-for="(item, index) in competencies.items"
						:key="index"
						class="inline"
						v-text="
							index < competencies.items.length - 1
								? item + ', '
								: item
						"
					></li>
				</ul>
			</div>
		</template>
	</ListItem>
</template>

<script>
import HeadingX from '~/components/shared/HeadingX';
import ListItem from '~/components/main/ListItem';
import AreaReferences from '~/components/main/AreaReferences';
import { nuxtImgSizes } from '~/assets/js/utilities/helpers';

export default {
	name: 'ListItemEmployee',

	components: {
		HeadingX,
		ListItem,
		AreaReferences,
	},

	props: {
		...ListItem.props,

		// Explicitly used props
		theme: {
			type: String,
			default: 'red',
		},

		image: {
			type: Object,
			default: null,
		},

		details: {
			type: Object,
			default: () => ({}),
		},
	},

	computed: {
		computedTitle() {
			if (Array.isArray(this.details?.title)) {
				return this.details.title.map((title, index) => {
					if (index < this.details.title.length - 1) {
						return `${title}, `;
					}
					return title;
				});
			}
			return this.details?.title || '';
		},

		computedPhone() {
			return (
				this.details?.phone?.text &&
				this.details.phone.link &&
				this.details.phone
			);
		},

		computedEmail() {
			return (
				this.details?.email?.text &&
				this.details.email.link &&
				this.details.email
			);
		},

		computedDepartments() {
			return (
				this.details?.departments?.length &&
				this.details.departments
					.map((department) => department.name)
					.join(', ')
			);
		},

		computedLocation() {
			return this.details?.location?.name;
		},

		areas() {
			return this.details?.areas?.length ? this.details?.areas : null;
		},
		competencies() {
			return this.details?.competencies?.items?.length
				? this.details?.competencies
				: null;
		},

		imageLoadColor() {
			if ((this.computedTheme || this.theme) === 'red') {
				return '#E5F4F5';
			}
			return '#E5F4F5';
		},
	},

	methods: {
		nuxtImgSizes,
	},
};
</script>

<style lang="postcss">
.c-list-item-employee__image {
	overflow: hidden;
	aspect-ratio: 1;

	& .c-list-item-employee__photo {
		@apply w-full h-full rounded-full overflow-hidden;
		background: rgba(0, 0, 0, 0.04);
		clip-path: circle(50% at 50% 50%);

		& > *,
		& picture {
			@apply w-full h-full rounded-full overflow-hidden;
		}
		& picture {
			transition: scale 0.2s ease-in-out, clip-path 0.2s ease-in-out;
			clip-path: circle(50% at 50% 50%);
		}
	}
}

.c-list-item-employee__image-form {
	@apply relative isolate w-140 h-140;
	aspect-ratio: 1;
}

.c-list-item-employee:hover .c-list-item-employee__image picture,
.c-list-item-employee:focus-within .c-list-item-employee__image picture {
	scale: 1.05;
	clip-path: circle(47.5% at 50% 50%);
}

.c-list-item-employee__areas {
	margin-inline: -4px;
	width: calc(100% + 8px);
}
.c-list-item-employee__areas > :last-child {
	margin-right: 42px;
}
</style>
